import React, { useState } from 'react'
import type { HeadingLevel } from 'src/typings/headingTags'
import useLayoutSetter from 'src/hooks/useLayoutSetter'
import Slider from 'react-slick'
import ImageListSchema from 'src/components/common/ImageListSchema/ImageListSchema'

import AnimatedSliderSettings from './AnimatedSliderSettings'
import DynamicHeadingTags from '../../common/DynamicHeadingTags/DynamicHeadingTags'
import AnimatedCarouselItem from './AnimatedCarouselItem'
import './animated-carousel.scss'

export type CarouselProps = {
  src: string
  href: string
  alt: string
  label?: string
  promotion: boolean
  campaignEco?: string
  promotionId?: string
  promotionCategory?: string
  promotionName?: string
  promotionPosition?: string
}

type AnimatedCarouselProps = {
  id?: string
  title?: string
  headingTitle: HeadingLevel
  subtitle?: string
  tag?: string
  tagBackground: string
  tagTextColor: string
  animationSpeed: string
  labelBackground: string
  labelTextColor: string
  sizings: {
    imageWidth: number
    imageHeight: number
  }
  carousel: CarouselProps[]
  componentLayout?: UseLayoutSetterProps
  imageLoading: 'lazy' | 'eager' | undefined
}

function AnimatedCarousel({
  id,
  title,
  headingTitle,
  subtitle,
  tag,
  tagBackground,
  tagTextColor,
  animationSpeed,
  labelBackground,
  labelTextColor,
  sizings,
  carousel,
  componentLayout,
  imageLoading = 'lazy',
}: AnimatedCarouselProps) {
  const [promoViewEventTriggered, setPromoViewEventTriggered] = useState<
    Set<string>
  >(new Set())

  const settings = {
    ...AnimatedSliderSettings,
    autoplaySpeed: parseInt(animationSpeed, 10),
    speed: parseInt(animationSpeed, 10),
  }

  const layout = useLayoutSetter(componentLayout)

  const onPromoView = (promotionId: string) => {
    if (!promoViewEventTriggered.has(promotionId)) {
      setPromoViewEventTriggered(
        (prevState) => new Set(prevState.add(promotionId))
      )
    }
  }

  return (
    <>
      <div className={`animated-carousel ${layout}`} id={id}>
        <div className="animated-carousel__titles">
          <div
            className="animated-carousel-tag"
            style={{ backgroundColor: tagBackground }}
          >
            <p style={{ color: tagTextColor }}>{tag}</p>
          </div>
          <DynamicHeadingTags
            as={headingTitle}
            className="animated-carousel-title"
          >
            {title}
          </DynamicHeadingTags>
          <p className="animated-carousel-subtitle">{subtitle}</p>
        </div>
        <Slider {...settings}>
          {carousel.map((item, index) => (
            <AnimatedCarouselItem
              key={index}
              item={item}
              index={index}
              sizings={sizings}
              imageLoading={imageLoading}
              labelBackground={labelBackground}
              labelTextColor={labelTextColor}
              onPromoView={onPromoView}
              promoViewEventTriggered={promoViewEventTriggered}
            />
          ))}
        </Slider>
      </div>
      <ImageListSchema itemList={carousel} name="Animated Carousel" />
    </>
  )
}

export default AnimatedCarousel
